import { useForm } from '@fukuri-admins/hooks/common';
import {
  Button,
  ButtonLink,
  CheckboxFieldController,
  RadioFieldController,
  SelectFieldController,
  TablePager,
} from '@fukuri-admins/ui/common';
import { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { getPushNotificationLink } from '~/utils/getLinks.ts';
import { getGroups } from '../../../api/groups';
import { ContentsHeader } from '../../../components/common/ContentsHeader/index.tsx';
import { ClientType } from '../../../types/resources/ClientType';
import { DeliveryTarget, NotificationForm } from '../../../types/resources/Notification';
import { ActionTableSortParamsType } from '../../../types/table/ActionTableSortParamsType.ts';
import { ClientList } from './ClientList';
import styles from './styles.module.scss';

type Props = {
  fetchClientsData: ClientType[];
  setFetchClientsData: (fetchClientsData: ClientType[]) => void;
  sortParams: ActionTableSortParamsType;
  setSortParams: (sortParams: ActionTableSortParamsType) => void;
  isLoading: boolean;
  handleFormSubmit: (data: NotificationForm) => void;
  selectedClients: ClientType[];
  setSelectedClients: React.Dispatch<React.SetStateAction<ClientType[]>>;
  pagerProps: React.ComponentProps<typeof TablePager>;
  onSearch: (name: string) => void;
};

export const CreateForm: React.FC<Props> = ({
  fetchClientsData,
  setFetchClientsData,
  sortParams,
  setSortParams,
  isLoading,
  handleFormSubmit,
  selectedClients,
  setSelectedClients,
  pagerProps,
  onSearch,
}) => {
  const [groupOptions, setGroupOptions] = useState<{ label: string; value: string }[]>([]);

  const formMethods = useForm<NotificationForm>({
    mode: 'onChange',
    defaultValues: {
      title: '',
      isImmediate: 'immediate',
      deliveryTarget: 'all',
      excludedTargets: [],
    },
  });
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = formMethods;

  const isImmediate = watch('isImmediate');
  const deliveryTarget = watch('deliveryTarget') as DeliveryTarget;
  useEffect(() => {
    const fetchGroups = async () => {
      const groups = await getGroups();
      setGroupOptions(groups.map(g => ({ label: g.label, value: String(g.value) })));
    };
    fetchGroups();
  }, []);

  const selectedMinutesOptions = [
    { label: '00', value: '00' },
    { label: '15', value: '15' },
    { label: '30', value: '30' },
    { label: '45', value: '45' },
  ];

  const pushNotificationValidationRules = {
    title: {
      required: 'タイトルは必須です',
    },
    body: {
      required: '本文は必須です',
    },
    link_url: {
      required: 'URLは必須です',
      pattern: {
        value: /^https:\/\/[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*(\.[a-zA-Z]{2,})(\/[^\s]*)?$/,
        message: '有効なURLを入力してください',
      },
    },
    category: {
      required: '配信対象は必須です',
    },
    selectedDate: {
      pattern: {
        value: /^\d{4}-\d{2}-\d{2}$/,
        message: '有効な日付を入力してください (YYYY-MM-DD形式)',
      },
    },
    selectedTime: {
      min: {
        value: 0,
        message: '配信時間は0以上の数値でなければなりません',
      },
      max: {
        value: 23,
        message: '配信時間は23以下の数値でなければなりません',
      },
    },
  };

  return (
    <div>
      <div className={styles.pushNotificationContent}>
        <ContentsHeader title='プッシュ通知の新規作成' />
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div className={styles.formContainer}>
              <div className={styles.formTitle}>
                タイトル <span className={styles.symbol}>*</span>
              </div>
              <input
                {...register('title', pushNotificationValidationRules.title)}
                placeholder='タイトルを入力'
                className={styles.input}
              />
              {errors.title && <span className={styles.error}>{errors.title.message}</span>}
            </div>
            <div className={styles.formContainer}>
              <div className={styles.formTitle}>
                本文 <span className={styles.symbol}>*</span>
              </div>
              <textarea
                {...register('body', pushNotificationValidationRules.body)}
                placeholder='本文を入力'
                className={styles.input}
                rows={6}
              />
              {errors.body && <span className={styles.error}>{errors.body.message}</span>}
            </div>
            <div className={styles.formContainer}>
              <div className={styles.formTitle}>
                URL <span className={styles.symbol}>*</span>
              </div>
              <input
                {...register('link_url', pushNotificationValidationRules.link_url)}
                placeholder='https://fukuri-app.jp'
                className={styles.input}
              />
              {errors.link_url && <span className={styles.error}>{errors.link_url.message}</span>}
            </div>
            <div className={styles.formContainer}>
              <RadioFieldController
                name='isImmediate'
                fieldWrapperProps={{
                  labelType: 'required',
                  labelText: '配信日時',
                }}
                radioGroup={{
                  direction: 'column',
                  gap: 12,
                  options: [
                    { value: 'immediate', label: '今すぐ配信する' },
                    { value: 'scheduled', label: '配信予約する' },
                  ],
                }}
              />
              {isImmediate === 'scheduled' && (
                <div className={styles.registrationScheduleDatetimeContainer}>
                  <input
                    {...register('selectedDate', {
                      ...pushNotificationValidationRules.selectedDate,
                      required: isImmediate === 'scheduled' ? '配信日は必須です' : false,
                    })}
                    placeholder='2023-10-01'
                    className={styles.input}
                    type='date'
                  />
                  <input
                    {...register('selectedTime', {
                      ...pushNotificationValidationRules.selectedTime,
                      required: isImmediate === 'scheduled' ? '配信時間は必須です' : false,
                    })}
                    placeholder='12'
                    min='0'
                    max='23'
                    className={styles.input}
                    type='number'
                  />
                  時
                  <select
                    className={styles.select}
                    {...register('selectedMinute')}
                    defaultValue='00'
                  >
                    {selectedMinutesOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  分
                </div>
              )}
              {errors.selectedDate && (
                <div className={styles.error}>{errors.selectedDate.message}</div>
              )}
              {errors.selectedTime && (
                <div className={styles.error}>{errors.selectedTime.message}</div>
              )}
            </div>
            <div className={styles.formContainer}>
              <RadioFieldController
                name='deliveryTarget'
                fieldWrapperProps={{
                  labelType: 'required',
                  labelText: '配信先',
                }}
                radioGroup={{
                  direction: 'column',
                  gap: 12,
                  options: [
                    { value: 'all', label: 'すべて' },
                    ...(groupOptions.length > 0
                      ? [{ value: 'group', label: 'グループから選択' }]
                      : []),
                    { value: 'manual', label: '手動で選択' },
                  ],
                }}
              />
              {deliveryTarget === 'group' && groupOptions.length > 0 && (
                <div className={styles.groupSelectContainer}>
                  <SelectFieldController
                    name='selectedGroupId'
                    fieldWrapperProps={{
                      labelText: '',
                    }}
                    select={{
                      placeholder: 'グループを選択してください',
                      options: groupOptions,
                    }}
                  />
                </div>
              )}
            </div>
            <div className={styles.formContainer}>
              <CheckboxFieldController
                name='excludedTargets'
                fieldWrapperProps={{ labelText: '配信から除外する対象' }}
                options={[{ value: 'family_users', label: '家族ユーザー' }]}
              />
            </div>
            {deliveryTarget === 'manual' && (
              <ClientList
                fetchClientsData={fetchClientsData}
                setFetchClientsData={setFetchClientsData}
                sortParams={sortParams}
                setSortParams={setSortParams}
                isLoading={isLoading}
                selectedClients={selectedClients}
                setSelectedClients={setSelectedClients}
                pagerProps={pagerProps}
                onSearch={onSearch}
              />
            )}
            <div className={styles.buttonContainer}>
              <ButtonLink variant='outline' to={getPushNotificationLink()}>
                キャンセル
              </ButtonLink>
              <Button variant='primary' type='submit'>
                配信する
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};
